import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { Button } from "../components";
import {
  fetchForecastScenarios,
  saveForecastScenario,
  deleteForecastScenario,
} from "../utils/db";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CAP_DEFAULT = 80;
const f$ = (v) => "$" + Math.round(v).toLocaleString("en-US");

// ─── Slider with editable value ───────────────────────────────────────────────
// Click the value to type any number beyond the slider range
const Slider = ({ label, hint, value, min, max, step = 1, onChange, formatDisplay, color = COLORS.primary, prefix = "", suffix = "" }) => {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState("");
  const inputRef = useRef(null);

  const displayed = formatDisplay ? formatDisplay(value) : `${prefix}${value}${suffix}`;

  const startEdit = () => {
    setRaw(String(value));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 20);
  };

  const commitEdit = () => {
    const n = parseFloat(raw.replace(/[^0-9.-]/g, ""));
    if (!isNaN(n) && n >= 0) onChange(n);
    setEditing(false);
  };

  // clamp slider value to its range for display, but allow raw value beyond
  const sliderVal = Math.min(Math.max(value, min), max);

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{label}</div>
          {hint && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>{hint}</div>}
        </div>
        {editing ? (
          <input
            ref={inputRef}
            value={raw}
            onChange={e => setRaw(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(false); }}
            style={{ width: 90, padding: "2px 8px", borderRadius: 6, border: `1.5px solid ${color}`, fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color, textAlign: "right", background: COLORS.card, outline: "none" }}
          />
        ) : (
          <span
            onClick={startEdit}
            title="Click to type a custom value"
            style={{ fontSize: 15, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, marginLeft: 12, cursor: "text", borderBottom: `1px dashed ${color}55`, paddingBottom: 1 }}>
            {displayed}
          </span>
        )}
      </div>
      <input type="range" min={min} max={max} step={step} value={sliderVal}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", cursor: "pointer", accentColor: color }} />
      {value > max && (
        <div style={{ fontSize: 10, color: COLORS.warning, marginTop: 2 }}>
          Value {displayed} exceeds slider max — drag slider or click value to adjust
        </div>
      )}
    </div>
  );
};

// ─── Metric card ──────────────────────────────────────────────────────────────
const Met = ({ label, value, sub, color = COLORS.text, bg = COLORS.bg }) => (
  <div style={{ background: bg, borderRadius: 12, padding: "12px 14px", border: `1px solid ${COLORS.border}` }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: "'Nunito', sans-serif" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Inline editable student count ───────────────────────────────────────────
const EditableCount = ({ value, original, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState("");
  const ref = useRef(null);
  const delta = value - original;
  const start = () => { setRaw(String(value)); setEditing(true); setTimeout(() => ref.current?.select(), 20); };
  const commit = () => {
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 0) onChange(n);
    setEditing(false);
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {delta !== 0 && <span style={{ fontSize: 10, fontWeight: 700, color: delta > 0 ? COLORS.accent : COLORS.danger }}>{delta > 0 ? "+" : ""}{delta}</span>}
      {editing ? (
        <input ref={ref} value={raw} onChange={e => setRaw(e.target.value)}
          onBlur={commit} onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          style={{ width: 70, padding: "2px 7px", borderRadius: 6, border: `1.5px solid ${COLORS.primary}`, fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: COLORS.primary, background: COLORS.card, outline: "none", textAlign: "right" }} />
      ) : (
        <span onClick={start} title="Click to edit student count"
          style={{ fontSize: 11, color: delta !== 0 ? COLORS.primary : COLORS.textMuted, fontWeight: delta !== 0 ? 700 : 400, cursor: "text", borderBottom: `1px dashed ${delta !== 0 ? COLORS.primary : COLORS.textMuted}55`, paddingBottom: 1 }}>
          {value} students
        </span>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ForecastingPage = ({ students = [], classrooms = [], tuitionPlans = [], currentUser, schoolId, isDemo }) => {
  const nowMonth = new Date().getMonth();

  useEffect(() => {
    if (!window.XLSX) {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      document.head.appendChild(s);
    }
    if (!window.jspdf) {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      document.head.appendChild(s);
    }
  }, []);

  const totalCap = useMemo(() =>
    classrooms.reduce((s, c) => s + (parseInt(c.capacity) || 15), 0) || CAP_DEFAULT,
  [classrooms]);

  const getPlanForStudent = useCallback((s) => {
    if (s?.tuitionPlanId) return tuitionPlans.find(p => p.id === s.tuitionPlanId) || null;
    const c = classrooms.find(cl => cl.id === s?.classroomId);
    if (c?.tuitionPlanId) return tuitionPlans.find(p => p.id === c.tuitionPlanId) || null;
    return null;
  }, [classrooms, tuitionPlans]);

  // real plans from DB
  const realPlans = useMemo(() => {
    if (tuitionPlans.length > 0) {
      return tuitionPlans.map(p => ({
        id: p.id, name: p.name,
        rate: parseFloat(p.monthlyRate) || 0,
        students: students.filter(s => getPlanForStudent(s)?.id === p.id).length,
        isTest: false,
      })).filter(p => p.rate > 0);
    }
    return [
      { id: 1, name: "Infant Full-Time",    rate: 1800, students: 12, isTest: false },
      { id: 2, name: "Toddler Full-Time",   rate: 1600, students: 18, isTest: false },
      { id: 3, name: "Preschool Full-Time", rate: 1200, students: 20, isTest: false },
      { id: 4, name: "Pre-K Full-Time",     rate: 1400, students: 10, isTest: false },
    ];
  }, [tuitionPlans, students, getPlanForStudent]);

  // test plans — forecast-only, never touch DB
  const [testPlans, setTestPlans] = useState([]);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [newPlanForm, setNewPlanForm] = useState({ name: "", rate: "", students: "" });
  const [newPlanErrors, setNewPlanErrors] = useState({});

  const addTestPlan = () => {
    const errs = {};
    if (!newPlanForm.name.trim()) errs.name = "Required";
    if (!newPlanForm.rate || parseFloat(newPlanForm.rate) <= 0) errs.rate = "Required";
    if (!newPlanForm.students || parseInt(newPlanForm.students) < 0) errs.students = "Required";
    setNewPlanErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const id = `test_${Date.now()}`;
    setTestPlans(prev => [...prev, {
      id, name: newPlanForm.name.trim(),
      rate: parseFloat(newPlanForm.rate),
      students: parseInt(newPlanForm.students),
      isTest: true,
    }]);
    setOverrides(prev => ({ ...prev, [id]: parseFloat(newPlanForm.rate) }));
    setNewPlanForm({ name: "", rate: "", students: "" });
    setNewPlanErrors({});
    setShowAddPlan(false);
    showToast(`Test plan "${newPlanForm.name.trim()}" added`);
  };

  const removeTestPlan = (id) => {
    setTestPlans(prev => prev.filter(p => p.id !== id));
    setOverrides(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  // combined plan list
  const planList = useMemo(() => [...realPlans, ...testPlans], [realPlans, testPlans]);

  // slider state
  const [overrides,  setOverrides]  = useState(() => Object.fromEntries(realPlans.map(p => [p.id, p.rate])));
  const [studentOverrides, setStudentOverrides] = useState({});
  const [sibDisc,    setSibDisc]    = useState(10);
  const [staffDisc,  setStaffDisc]  = useState(15);
  const [regFee,     setRegFee]     = useState(200);
  const [occ,        setOcc]        = useState(85);
  const [churn,      setChurn]      = useState(3);
  const [newEnroll,  setNewEnroll]  = useState(3);
  const [growRate,   setGrowRate]   = useState(3);
  const [latePay,    setLatePay]    = useState(8);
  const [expenses,   setExpenses]   = useState(28000);
  const [tab,        setTab]        = useState("whatif");

  const [scenarios,        setScenarios]        = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(true);
  const [showSave,         setShowSave]         = useState(false);
  const [scenarioName,     setScenarioName]     = useState("");
  const [saving,           setSaving]           = useState(false);
  const [toast,            setToast]            = useState("");
  const nameRef = useRef(null);

  // ── derived counts (after all state declared) ───────────────────────────────
  const enrolledCount = realPlans.reduce((s, p) => s + (studentOverrides[p.id] ?? p.students), 0) || (students.length || realPlans.reduce((s, p) => s + p.students, 0));
  const totalEnrolledIncTest = enrolledCount + testPlans.reduce((s, p) => s + p.students, 0);

  useEffect(() => {
    if (!schoolId || isDemo) { setScenariosLoading(false); return; }
    fetchForecastScenarios(schoolId).then(data => {
      if (data) setScenarios(data);
      setScenariosLoading(false);
    });
  }, [schoolId, isDemo]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  // ── revenue calculations ────────────────────────────────────────────────────
  const baseRevenue = useMemo(() =>
    realPlans.reduce((s, p) => s + p.rate * (1 - sibDisc/100) * (1 - staffDisc/100) * (studentOverrides[p.id] ?? p.students), 0),
  [realPlans, sibDisc, staffDisc, studentOverrides]);

  const whatIfRevenue = useMemo(() =>
    planList.reduce((s, p) => s + (overrides[p.id] ?? p.rate) * (1 - sibDisc/100) * (1 - staffDisc/100) * (p.isTest ? p.students : (studentOverrides[p.id] ?? p.students)), 0),
  [planList, overrides, sibDisc, staffDisc, studentOverrides]);

  const delta = whatIfRevenue - baseRevenue;

  // ── projection ──────────────────────────────────────────────────────────────
  const projection = useMemo(() => {
    const cap = Math.round(totalCap * occ / 100);
    const avgRate = totalEnrolledIncTest > 0 ? whatIfRevenue / totalEnrolledIncTest : 0;
    let s = totalEnrolledIncTest, mrev = whatIfRevenue;
    return Array.from({ length: 12 }, (_, i) => {
      const ch = Math.round(s * churn / 100);
      s = Math.max(1, Math.min(cap, s - ch + newEnroll));
      mrev = s * avgRate * (1 + growRate / 100 / 12);
      const billed = Math.round(mrev + newEnroll * regFee);
      const collected = Math.round(billed * (1 - latePay / 100));
      return { label: MONTHS[(nowMonth + i) % 12], students: s, billed, collected };
    });
  }, [whatIfRevenue, totalEnrolledIncTest, occ, churn, newEnroll, growRate, latePay, regFee, totalCap, nowMonth]);

  const projBilled = projection.reduce((s, r) => s + r.billed, 0);
  const projColl   = projection.reduce((s, r) => s + r.collected, 0);
  const endStuds   = projection[projection.length - 1]?.students ?? totalEnrolledIncTest;

  // ── break-even ──────────────────────────────────────────────────────────────
  const avgPS      = totalEnrolledIncTest > 0 ? whatIfRevenue / totalEnrolledIncTest : 0;
  const beStudents = avgPS > 0 ? Math.ceil(expenses / avgPS) : 0;
  const surplus    = whatIfRevenue - expenses;
  const beOcc      = totalCap > 0 ? Math.round(beStudents / totalCap * 100) : 0;
  const profitable = totalEnrolledIncTest >= beStudents;
  const curOccPct  = Math.min((totalEnrolledIncTest / totalCap) * 100, 100);
  const markPct    = Math.min((beStudents / totalCap) * 100, 100);

  // ── scenario helpers ────────────────────────────────────────────────────────
  const getState = () => ({
    overrides: { ...overrides }, studentOverrides: { ...studentOverrides }, sibDisc, staffDisc, regFee,
    occ, churn, newEnroll, growRate, latePay, expenses,
    testPlans: testPlans.map(p => ({ ...p })),
  });

  const applyState = (st) => {
    setOverrides(st.overrides ?? {});
    setStudentOverrides(st.studentOverrides ?? {});
    setSibDisc(st.sibDisc ?? 10);    setStaffDisc(st.staffDisc ?? 15);
    setRegFee(st.regFee ?? 200);     setOcc(st.occ ?? 85);
    setChurn(st.churn ?? 3);         setNewEnroll(st.newEnroll ?? 3);
    setGrowRate(st.growRate ?? 3);   setLatePay(st.latePay ?? 8);
    setExpenses(st.expenses ?? 28000);
    setTestPlans(st.testPlans ?? []);
  };

  const handleSave = async () => {
    if (!scenarioName.trim()) return;
    setSaving(true);
    const payload = { name: scenarioName.trim(), state: getState(), wi: whatIfRevenue };
    if (!isDemo && schoolId) {
      const saved = await saveForecastScenario(payload, schoolId, currentUser?.id);
      if (saved) { setScenarios(prev => [saved, ...prev]); showToast(`"${saved.name}" saved`); }
      else { showToast("Save failed — please try again"); setSaving(false); return; }
    } else {
      setScenarios(prev => [{ id: Date.now(), ...payload, createdAt: new Date().toISOString() }, ...prev]);
      showToast(`"${payload.name}" saved (demo — not persisted)`);
    }
    setScenarioName(""); setShowSave(false); setSaving(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!isDemo && schoolId) await deleteForecastScenario(id);
    setScenarios(prev => prev.filter(s => s.id !== id));
  };

  // ── export Excel ────────────────────────────────────────────────────────────
  const exportExcel = () => {
    const XLSX = window.XLSX;
    if (!XLSX) { showToast("Excel library loading — try again in a moment"); return; }
    const wb = XLSX.utils.book_new();
    const summary = [
      ["ShrewCare Revenue Forecast"], ["Generated", new Date().toLocaleDateString()], [],
      ["SUMMARY"],
      ["Current monthly revenue", baseRevenue], ["What-if monthly revenue", whatIfRevenue],
      ["Monthly change", delta], ["Annual what-if", whatIfRevenue * 12], [],
      ["TUITION PLAN RATES","Original","Adjusted","Students","Type"],
      ...planList.map(p => [p.name, p.isTest ? "N/A (test)" : p.rate, overrides[p.id] ?? p.rate, p.students, p.isTest ? "Test (forecast only)" : "Live"]), [],
      ["SETTINGS","Value"],
      ["Sibling discount %", sibDisc], ["Staff discount %", staffDisc],
      ["Registration fee", regFee], ["Target occupancy %", occ],
      ["Monthly churn %", churn], ["New enrollments/mo", newEnroll],
      ["Tuition growth %/yr", growRate], ["Late payment %", latePay],
      ["Monthly expenses", expenses],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
    const projRows = [["Month","Students","Billed","Collected","vs Today"],
      ...projection.map(r => [r.label, r.students, r.billed, r.collected, r.collected - baseRevenue])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(projRows), "12-Month Projection");
    if (scenarios.length > 0) {
      const scRows = [["Scenario","Monthly Revenue","vs Base","Annual","Saved"],
        ...scenarios.map(s => [s.name, s.wi, s.wi - baseRevenue, s.wi * 12, new Date(s.createdAt).toLocaleDateString()])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scRows), "Saved Scenarios");
    }
    XLSX.writeFile(wb, `ShrewCare_Forecast_${new Date().toISOString().slice(0,10)}.xlsx`);
    showToast("Excel exported");
  };

  // ── export PDF ──────────────────────────────────────────────────────────────
  const exportPDF = () => {
    const jspdf = window.jspdf;
    if (!jspdf) { showToast("PDF library loading — try again in a moment"); return; }
    const { jsPDF } = jspdf;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210, m = 16; let y = m;
    doc.setFillColor(15,28,55); doc.rect(0,0,W,28,"F");
    doc.setTextColor(255,255,255); doc.setFontSize(16); doc.setFont(undefined,"bold");
    doc.text("ShrewCare — Revenue Forecast", m, 16);
    doc.setFontSize(9); doc.setFont(undefined,"normal");
    doc.text("Generated " + new Date().toLocaleDateString(), m, 23);
    y = 36;
    const bw = (W - m*2 - 9) / 4;
    [[f$(baseRevenue),"Current monthly",[30,60,130]],[f$(whatIfRevenue),"What-if monthly",delta>=0?[20,120,70]:[180,40,40]],[(delta>=0?"+":"")+f$(delta),"Monthly change",delta>=0?[20,120,70]:[180,40,40]],[f$(whatIfRevenue*12),"Annual what-if",[30,60,130]]].forEach(([val,lbl,col],i) => {
      const x = m + i*(bw+3);
      doc.setFillColor(245,247,250); doc.roundedRect(x,y,bw,18,2,2,"F");
      doc.setTextColor(100,110,130); doc.setFontSize(7); doc.setFont(undefined,"normal");
      doc.text(lbl.toUpperCase(), x+3, y+6);
      doc.setTextColor(...col); doc.setFontSize(11); doc.setFont(undefined,"bold");
      doc.text(val, x+3, y+14);
    });
    y += 26;
    doc.setTextColor(15,28,55); doc.setFontSize(10); doc.setFont(undefined,"bold");
    doc.text("Tuition Plan Rates", m, y); y += 5;
    doc.setFillColor(240,244,248); doc.rect(m,y,W-m*2,7,"F");
    doc.setTextColor(100,110,130); doc.setFontSize(7); doc.setFont(undefined,"normal");
    ["Plan","Original","Adjusted","Students","Type"].forEach((h,i) => doc.text(h,[m+2,m+55,m+88,m+118,m+135][i],y+5));
    y += 8;
    planList.forEach((p,i) => {
      if(i%2===0){doc.setFillColor(250,251,252);doc.rect(m,y-1,W-m*2,8,"F");}
      const adj = overrides[p.id] ?? p.rate;
      doc.setTextColor(30,40,60); doc.setFontSize(8); doc.setFont(undefined,"normal");
      doc.text(p.name,m+2,y+5);
      doc.text(p.isTest ? "Test" : f$(p.rate),m+55,y+5);
      if(!p.isTest && adj!==p.rate) doc.setTextColor(adj>p.rate?20:180,adj>p.rate?120:40,adj>p.rate?70:40);
      doc.text(f$(adj),m+88,y+5);
      doc.setTextColor(30,40,60); doc.text(String(p.students),m+118,y+5);
      if(p.isTest){doc.setTextColor(180,100,20);doc.text("Test only",m+135,y+5);}
      else{doc.setTextColor(20,120,70);doc.text("Live",m+135,y+5);}
      y += 8;
    });
    y += 6;
    doc.setTextColor(15,28,55); doc.setFontSize(10); doc.setFont(undefined,"bold");
    doc.text("12-Month Projection", m, y); y += 5;
    doc.setFillColor(240,244,248); doc.rect(m,y,W-m*2,7,"F");
    doc.setTextColor(100,110,130); doc.setFontSize(7); doc.setFont(undefined,"normal");
    ["Month","Students","Billed","Collected","vs Today"].forEach((h,i) => doc.text(h,[m+2,m+30,m+55,m+85,m+115][i],y+5));
    y += 8;
    projection.forEach((r,i) => {
      if(y>272){doc.addPage();y=m;}
      if(i%2===0){doc.setFillColor(250,251,252);doc.rect(m,y-1,W-m*2,8,"F");}
      const d = r.collected - baseRevenue;
      doc.setTextColor(30,40,60); doc.setFontSize(8); doc.setFont(undefined,"normal");
      doc.text(r.label,m+2,y+5); doc.text(String(r.students),m+30,y+5); doc.text(f$(r.billed),m+55,y+5);
      doc.setTextColor(20,120,70); doc.text(f$(r.collected),m+85,y+5);
      doc.setTextColor(d>=0?20:180,d>=0?120:40,d>=0?70:40); doc.text((d>=0?"+":"")+f$(d),m+115,y+5);
      y += 8;
    });
    if(scenarios.length>0){
      if(y>250){doc.addPage();y=m;}
      y+=4;
      doc.setTextColor(15,28,55);doc.setFontSize(10);doc.setFont(undefined,"bold");
      doc.text("Saved Scenarios",m,y);y+=5;
      doc.setFillColor(240,244,248);doc.rect(m,y,W-m*2,7,"F");
      doc.setTextColor(100,110,130);doc.setFontSize(7);doc.setFont(undefined,"normal");
      ["Scenario","Monthly Revenue","vs Base","Annual","Saved"].forEach((h,i)=>doc.text(h,[m+2,m+55,m+90,m+115,m+145][i],y+5));
      y+=8;
      scenarios.forEach((sc,i)=>{
        if(i%2===0){doc.setFillColor(250,251,252);doc.rect(m,y-1,W-m*2,8,"F");}
        const d=sc.wi-baseRevenue;
        doc.setTextColor(30,40,60);doc.setFontSize(8);doc.setFont(undefined,"normal");
        doc.text(sc.name,m+2,y+5);doc.text(f$(sc.wi),m+55,y+5);
        doc.setTextColor(d>=0?20:180,d>=0?120:40,d>=0?70:40);doc.text((d>=0?"+":"")+f$(d),m+90,y+5);
        doc.setTextColor(30,40,60);doc.text(f$(sc.wi*12),m+115,y+5);
        doc.text(new Date(sc.createdAt).toLocaleDateString(),m+145,y+5);
        y+=8;
      });
    }
    doc.setFontSize(7);doc.setTextColor(150,155,165);
    doc.text("ShrewCare Forecasting Tool · "+new Date().toLocaleDateString(), m, 290);
    doc.save(`ShrewCare_Forecast_${new Date().toISOString().slice(0,10)}.pdf`);
    showToast("PDF exported");
  };

  // ── style helpers ───────────────────────────────────────────────────────────
  const panel = { background: COLORS.card, borderRadius: 16, border: `1px solid ${COLORS.border}`, padding: "20px 22px", marginBottom: 12 };
  const tabBtn = (id) => ({ padding: "9px 22px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", background: tab===id ? COLORS.primary : "transparent", color: tab===id ? "#fff" : COLORS.textMuted, transition: "all .15s" });
  const secLabel = (text) => <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>{text}</div>;
  const twoCol = { display: "grid", gridTemplateColumns: "300px 1fr", gap: 16 };
  const inputStyle = { width: "100%", padding: "9px 12px", borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.inputBg || COLORS.bg, color: COLORS.text, boxSizing: "border-box" };
  const errStyle = { fontSize: 11, color: COLORS.danger, marginTop: 3 };

  return (
    <div style={{ fontFamily: "'Nunito', 'DM Sans', sans-serif" }}>

      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "10px 22px", borderRadius: 20, background: "#1e293b", color: "#fff", fontSize: 13, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,.2)", pointerEvents: "none" }}>
          ✓ {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: COLORS.navy, fontFamily: "'Nunito', sans-serif" }}>Revenue Forecasting</h2>
          <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 13 }}>Model tuition changes, project growth, and find your break-even — before making any real changes.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => { setShowSave(true); setTimeout(() => nameRef.current?.focus(), 50); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.text, fontFamily: "inherit" }}>
            <Icon name="edit" size={14} /> Save scenario
          </button>
          <button onClick={exportExcel}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: `1px solid ${COLORS.accent}33`, background: COLORS.accentPale, cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.accent, fontFamily: "inherit" }}>
            <Icon name="download" size={14} /> Export Excel
          </button>
          <button onClick={exportPDF}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: `1px solid ${COLORS.primary}33`, background: COLORS.primaryPale, cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit" }}>
            <Icon name="download" size={14} /> Export PDF
          </button>
        </div>
      </div>

      {/* Saved scenarios bar */}
      <div style={{ background: COLORS.bg, borderRadius: 12, padding: "10px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", border: `1px solid ${COLORS.border}`, minHeight: 44 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Saved:</span>
        {scenariosLoading && <span style={{ fontSize: 12, color: COLORS.textMuted }}>Loading...</span>}
        {!scenariosLoading && scenarios.length === 0 && (
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>No scenarios yet — adjust the sliders and click "Save scenario".</span>
        )}
        {scenarios.map(sc => (
          <div key={sc.id}
            onClick={() => { applyState(sc.state); showToast("Loaded: " + sc.name); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 12, fontWeight: 600, transition: "border-color .15s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.primary}
            onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}>
            <span style={{ color: COLORS.text }}>{sc.name}</span>
            <span style={{ fontSize: 10, color: COLORS.textMuted }}>{f$(sc.wi)}/mo</span>
            <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 2 }}>
              {new Date(sc.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
            <span onClick={e => handleDelete(sc.id, e)} title="Delete scenario"
              style={{ color: COLORS.textMuted, cursor: "pointer", fontSize: 16, lineHeight: 1, marginLeft: 2, padding: "0 2px" }}
              onMouseEnter={e => e.currentTarget.style.color = COLORS.danger}
              onMouseLeave={e => e.currentTarget.style.color = COLORS.textMuted}>×</span>
          </div>
        ))}
      </div>

      {/* Save modal */}
      {showSave && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: COLORS.card, borderRadius: 20, padding: 32, width: 380, maxWidth: "90vw", border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy, marginBottom: 6, fontFamily: "'Nunito', sans-serif" }}>Save scenario</div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>
              Give this configuration a name. It will be saved to your school's account and available next time you log in.
              {testPlans.length > 0 && <span style={{ color: COLORS.warning }}> Includes {testPlans.length} test plan{testPlans.length > 1 ? "s" : ""}.</span>}
            </div>
            <input ref={nameRef} value={scenarioName} onChange={e => setScenarioName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !saving && handleSave()}
              placeholder="e.g. Optimistic Q3, Rate increase 5%, Conservative"
              maxLength={60} style={{ ...inputStyle, marginBottom: 8 }} />
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16 }}>
              What-if monthly: <strong style={{ color: COLORS.primary }}>{f$(whatIfRevenue)}/mo</strong>
              {delta !== 0 && <span style={{ marginLeft: 8, color: delta > 0 ? COLORS.accent : COLORS.danger, fontWeight: 700 }}>{delta > 0 ? "+" : ""}{f$(delta)} vs today</span>}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="secondary" onClick={() => { setShowSave(false); setScenarioName(""); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !scenarioName.trim()}>{saving ? "Saving..." : "Save"}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add test plan modal */}
      {showAddPlan && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: COLORS.card, borderRadius: 20, padding: 32, width: 380, maxWidth: "90vw", border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy, marginBottom: 4, fontFamily: "'Nunito', sans-serif" }}>Add test plan</div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 20 }}>
              Forecast-only — this plan won't be created in your school's billing. Use it to model a new tier or hypothetical pricing.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Plan name</label>
              <input value={newPlanForm.name} onChange={e => setNewPlanForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Summer Camp, Part-Time, New Infant Rate"
                style={inputStyle} />
              {newPlanErrors.name && <div style={errStyle}>{newPlanErrors.name}</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Monthly rate ($)</label>
                <input type="number" value={newPlanForm.rate} onChange={e => setNewPlanForm(f => ({ ...f, rate: e.target.value }))}
                  placeholder="1500" min="0" style={inputStyle} />
                {newPlanErrors.rate && <div style={errStyle}>{newPlanErrors.rate}</div>}
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Student count</label>
                <input type="number" value={newPlanForm.students} onChange={e => setNewPlanForm(f => ({ ...f, students: e.target.value }))}
                  placeholder="10" min="0" style={inputStyle} />
                {newPlanErrors.students && <div style={errStyle}>{newPlanErrors.students}</div>}
              </div>
            </div>
            {newPlanForm.rate && newPlanForm.students && (
              <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.primaryPale, fontSize: 12, color: COLORS.primary, fontWeight: 600, marginBottom: 16 }}>
                Adds {f$(parseFloat(newPlanForm.rate) * parseInt(newPlanForm.students))}/mo · {f$(parseFloat(newPlanForm.rate) * parseInt(newPlanForm.students) * 12)}/yr to your forecast
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="secondary" onClick={() => { setShowAddPlan(false); setNewPlanForm({ name: "", rate: "", students: "" }); setNewPlanErrors({}); }}>Cancel</Button>
              <Button onClick={addTestPlan}>Add to forecast</Button>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Met label="Current monthly"  value={f$(baseRevenue)}    sub={`${enrolledCount} enrolled students`} color={COLORS.primary} />
        <Met label="Current annual"   value={f$(baseRevenue*12)} sub="before any changes" color={COLORS.navy} />
        <Met label="Occupancy"        value={`${Math.round(enrolledCount/totalCap*100)}%`} sub={`${enrolledCount} of ${totalCap} seats`} color={Math.round(enrolledCount/totalCap*100)>=85?COLORS.accent:COLORS.warning} />
        <Met label="Avg per student"  value={f$(enrolledCount>0?baseRevenue/enrolledCount:0)} sub="monthly avg" color={COLORS.text} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, background: COLORS.bg, borderRadius: 12, padding: 4, width: "fit-content", marginBottom: 20, border: `1px solid ${COLORS.border}` }}>
        {[["whatif","What if"],["projection","12-month projection"],["breakeven","Break-even"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={tabBtn(id)}>{label}</button>
        ))}
      </div>

      {/* ── WHAT IF ─────────────────────────────────────────────────────────── */}
      {tab === "whatif" && (
        <div style={twoCol}>
          <div>
            <div style={panel}>
              {secLabel("Adjust tuition rates")}
              {/* Real plans */}
              {realPlans.map(p => {
                const v = overrides[p.id] ?? p.rate, d = v - p.rate;
                const col = d > 0 ? COLORS.accent : d < 0 ? COLORS.danger : COLORS.primary;
                const studs = studentOverrides[p.id] ?? p.students;
                const studsDelta = studs - p.students;
                return (
                  <div key={p.id} style={{ background: COLORS.bg, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{p.name}</span>
                      <EditableCount
                        value={studs}
                        original={p.students}
                        onChange={val => setStudentOverrides(prev => ({ ...prev, [p.id]: val }))}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>
                      Original: {f$(p.rate)}/mo
                      {d !== 0 && <span style={{ marginLeft: 8, fontWeight: 700, color: col }}>{d>0?"+":""}{f$(d)}/mo</span>}
                    </div>
                    <Slider label="Monthly rate" value={v} min={Math.round(p.rate*.5)} max={Math.round(p.rate*2)} step={10}
                      onChange={val => setOverrides(prev => ({ ...prev, [p.id]: val }))}
                      formatDisplay={f$} color={col} />
                    {d !== 0 && studs > 0 && (
                      <div style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 8, background: d>0?COLORS.accentPale:COLORS.dangerPale, color: col, display: "inline-block" }}>
                        {d>0?"+":""}{f$(d*studs)}/mo · {d>0?"+":""}{f$(d*studs*12)}/yr
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Test plans */}
              {testPlans.length > 0 && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, marginTop: 4 }}>
                    <div style={{ height: 1, flex: 1, background: COLORS.borderLight }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.warning, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>Test plans (forecast only)</span>
                    <div style={{ height: 1, flex: 1, background: COLORS.borderLight }} />
                  </div>
                  {testPlans.map(p => {
                    const v = overrides[p.id] ?? p.rate;
                    return (
                      <div key={p.id} style={{ background: COLORS.warningPale, borderRadius: 10, padding: "12px 14px", marginBottom: 10, border: `1px solid ${COLORS.warning}33` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{p.name}</span>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: COLORS.warningPale, color: COLORS.warning, border: `1px solid ${COLORS.warning}44` }}>TEST</span>
                          </div>
                          <button onClick={() => removeTestPlan(p.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 16, lineHeight: 1, padding: "0 2px" }}
                            title="Remove test plan">×</button>
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>{p.students} students · Base: {f$(p.rate)}/mo</div>
                        <Slider label="Monthly rate" value={v} min={Math.round(p.rate*.5)} max={Math.round(p.rate*2)} step={10}
                          onChange={val => setOverrides(prev => ({ ...prev, [p.id]: val }))}
                          formatDisplay={f$} color={COLORS.warning} />
                        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.warning }}>
                          Adds {f$(v*(1-sibDisc/100)*(1-staffDisc/100)*p.students)}/mo to forecast
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Add test plan button */}
              <button onClick={() => setShowAddPlan(true)}
                style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: `2px dashed ${COLORS.border}`, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, fontFamily: "inherit", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Icon name="plus" size={14} /> Add test plan
              </button>

              <div style={{ height: 1, background: COLORS.borderLight, margin: "16px 0" }} />
              {secLabel("Discounts")}
              <Slider label="Sibling discount" value={sibDisc} min={0} max={100} step={1} onChange={setSibDisc} formatDisplay={v => `${v}%`} suffix="%" color={COLORS.warning} />
              <Slider label="Staff discount" value={staffDisc} min={0} max={100} step={1} onChange={setStaffDisc} formatDisplay={v => `${v}%`} suffix="%" color={COLORS.warning} />
              <Slider label="Registration fee" hint="one-time per new enrollment" value={regFee} min={0} max={500} step={25} onChange={setRegFee} formatDisplay={f$} color={COLORS.primary} />
              <button onClick={() => { setOverrides(Object.fromEntries(planList.map(p => [p.id, p.rate]))); setStudentOverrides({}); }}
                style={{ width: "100%", padding: 9, borderRadius: 10, border: `1px solid ${COLORS.border}`, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, fontFamily: "inherit", marginTop: 4 }}>
                Reset all rates
              </button>
            </div>
          </div>
          <div>
            <div style={panel}>
              {secLabel("Revenue impact")}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                <Met label="What-if monthly" value={f$(whatIfRevenue)} color={whatIfRevenue>=baseRevenue?COLORS.accent:COLORS.danger} bg={COLORS.primaryPale} />
                <Met label="What-if annual" value={f$(whatIfRevenue*12)} color={COLORS.navy} />
                <Met label="Monthly change" value={`${delta>=0?"+":""}${f$(delta)}`} sub={`${delta>=0?"+":""}${f$(delta*12)}/yr`} color={delta>=0?COLORS.accent:COLORS.danger} bg={delta>=0?COLORS.accentPale:COLORS.dangerPale} />
                <Met label="Per-student avg" value={f$(totalEnrolledIncTest>0?whatIfRevenue/totalEnrolledIncTest:0)} sub={testPlans.length>0?"inc. test plans":"after discounts"} color={COLORS.primary} />
              </div>
              {testPlans.length > 0 && (
                <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.warningPale, border: `1px solid ${COLORS.warning}33`, marginBottom: 16, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: COLORS.warning }}>Test plans active:</span>
                  <span style={{ color: COLORS.textMuted, marginLeft: 6 }}>
                    {testPlans.length} plan{testPlans.length>1?"s":""} adding {f$(testPlans.reduce((s,p)=>(overrides[p.id]??p.rate)*(1-sibDisc/100)*(1-staffDisc/100)*p.students+s,0))}/mo to the forecast
                  </span>
                </div>
              )}
              {secLabel("Plan breakdown")}
              {planList.map(p => {
                let r = (overrides[p.id]??p.rate)*(1-sibDisc/100)*(1-staffDisc/100);
                const studsForCalc = p.isTest ? p.students : (studentOverrides[p.id] ?? p.students);
                const rev = r*studsForCalc, pct = whatIfRevenue>0?Math.round(rev/whatIfRevenue*100):0;
                return (
                  <div key={p.id} style={{ background: p.isTest ? COLORS.warningPale : COLORS.bg, borderRadius: 10, padding: "10px 14px", marginBottom: 8, border: p.isTest ? `1px solid ${COLORS.warning}33` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.navy }}>{p.name}</span>
                        {p.isTest && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 20, background: COLORS.warningPale, color: COLORS.warning, border: `1px solid ${COLORS.warning}44` }}>TEST</span>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: p.isTest?COLORS.warning:COLORS.accent }}>{f$(rev)}/mo</span>
                    </div>
                    <div style={{ height: 4, background: COLORS.borderLight, borderRadius: 2 }}>
                      <div style={{ width: `${pct}%`, height: "100%", borderRadius: 2, background: p.isTest?COLORS.warning:COLORS.primary, transition: "width .4s" }} />
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>{studsForCalc} students · {f$(r)}/mo · {pct}% of total</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── PROJECTION ─────────────────────────────────────────────────────── */}
      {tab === "projection" && (
        <div style={twoCol}>
          <div>
            <div style={panel}>
              {secLabel("Growth assumptions")}
              <Slider label="Target occupancy" hint={`cap: ${Math.round(totalCap*occ/100)} students`} value={occ} min={10} max={100} step={1} onChange={setOcc} formatDisplay={v=>`${v}%`} color={COLORS.primary} />
              <Slider label="Monthly churn" hint="% of students who leave each month" value={churn} min={0} max={100} step={0.5} onChange={setChurn} formatDisplay={v=>`${v}%`} color={COLORS.danger} />
              <Slider label="New enrollments / mo" value={newEnroll} min={0} max={20} step={1} onChange={setNewEnroll} formatDisplay={v=>`${v} students`} color={COLORS.accent} />
              <Slider label="Tuition growth rate" hint="annual rate increase" value={growRate} min={0} max={100} step={0.5} onChange={setGrowRate} formatDisplay={v=>`${v}%/yr`} color={COLORS.primary} />
              <Slider label="Late payment rate" hint="revenue not collected that month" value={latePay} min={0} max={100} step={1} onChange={setLatePay} formatDisplay={v=>`${v}%`} color={COLORS.warning} />
            </div>
          </div>
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
              <Met label="Projected annual billed" value={f$(projBilled)} color={COLORS.primary} bg={COLORS.primaryPale} />
              <Met label="Annual collected" value={f$(projColl)} sub={`${Math.round(projColl/projBilled*100)||0}% collection`} color={COLORS.accent} bg={COLORS.accentPale} />
              <Met label="Students at month 12" value={endStuds} sub={`started: ${totalEnrolledIncTest}`} color={endStuds>=totalEnrolledIncTest?COLORS.accent:COLORS.danger} />
            </div>
            <div style={panel}>
              {secLabel("Monthly revenue — next 12 months")}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 140, padding: "0 2px" }}>
                {(() => {
                  const maxB = Math.max(...projection.map(r => r.billed), whatIfRevenue, 1);
                  return projection.map((r, i) => {
                    const h = Math.max((r.billed/maxB)*100, 3);
                    const bg = r.billed>whatIfRevenue?COLORS.accent:r.billed<whatIfRevenue?COLORS.danger:COLORS.primary;
                    return (
                      <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3, height:"100%", justifyContent:"flex-end" }}>
                        <div style={{ fontSize:8, color:COLORS.textMuted, fontWeight:600 }}>{r.billed>=1000?"$"+Math.round(r.billed/1000)+"k":f$(r.billed)}</div>
                        <div style={{ width:"100%", borderRadius:"3px 3px 0 0", height:`${h}%`, background:bg, transition:"height .4s cubic-bezier(.34,1.2,.64,1)" }} />
                        <div style={{ fontSize:9, color:COLORS.textMuted }}>{r.label}</div>
                      </div>
                    );
                  });
                })()}
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:COLORS.textMuted, marginTop:6 }}>
                <span>{MONTHS[nowMonth]} (today)</span><span>{MONTHS[(nowMonth+11)%12]} (month 12)</span>
              </div>
            </div>
            <div style={panel}>
              {secLabel("Month-by-month")}
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead><tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                    {["Month","Students","Billed","Collected","vs today"].map(h => (
                      <th key={h} style={{ padding:"5px 8px", textAlign:"left", fontSize:10, fontWeight:700, color:COLORS.textMuted, textTransform:"uppercase" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {projection.map((r,i) => {
                      const d = r.collected - baseRevenue;
                      return (
                        <tr key={i} style={{ borderBottom:`1px solid ${COLORS.borderLight}` }}>
                          <td style={{ padding:"6px 8px", fontWeight:600 }}>{r.label}</td>
                          <td style={{ padding:"6px 8px", color:COLORS.textMuted }}>{r.students}</td>
                          <td style={{ padding:"6px 8px" }}>{f$(r.billed)}</td>
                          <td style={{ padding:"6px 8px", fontWeight:700, color:COLORS.accent }}>{f$(r.collected)}</td>
                          <td style={{ padding:"6px 8px", fontWeight:700, color:d>=0?COLORS.accent:COLORS.danger }}>{d>=0?"+":""}{f$(d)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── BREAK-EVEN ──────────────────────────────────────────────────────── */}
      {tab === "breakeven" && (
        <div style={twoCol}>
          <div>
            <div style={panel}>
              {secLabel("Monthly expenses")}
              <Slider label="Total monthly expenses" hint="salaries, rent, supplies, software" value={expenses} min={1000} max={50000} step={250} onChange={setExpenses} formatDisplay={f$} color={COLORS.danger} />
              {secLabel("Benchmarks")}
              <div style={{ background:COLORS.bg, borderRadius:10, padding:"10px 14px" }}>
                {[["Solo home daycare (6 kids)",3500],["Small center (20 kids)",12000],["Mid-size center (50 kids)",28000],["Large center (100 kids)",55000]].map(([label,val]) => (
                  <div key={val} onClick={() => setExpenses(val)} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${COLORS.borderLight}`, cursor:"pointer", fontSize:12 }}>
                    <span style={{ color:COLORS.textMuted }}>{label}</span>
                    <span style={{ color:COLORS.primary, fontWeight:700 }}>{f$(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div style={{ textAlign:"center", padding:"1.5rem", borderRadius:16, border:`1px solid ${profitable?COLORS.accent:COLORS.danger}`, background:profitable?COLORS.accentPale:COLORS.dangerPale, marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:COLORS.textMuted, textTransform:"uppercase", letterSpacing:1 }}>Break-even point</div>
              <div style={{ fontSize:56, fontWeight:800, color:profitable?COLORS.accent:COLORS.danger, lineHeight:1, margin:"8px 0", fontFamily:"'Nunito', sans-serif" }}>{beStudents}</div>
              <div style={{ fontSize:13, color:COLORS.textMuted, marginBottom:6 }}>students needed to cover {f$(expenses)}/mo</div>
              <div style={{ fontSize:13, fontWeight:700, color:profitable?COLORS.accent:COLORS.danger }}>
                {profitable?`You're there — ${totalEnrolledIncTest-beStudents} students above break-even.`:`${beStudents-totalEnrolledIncTest} more students needed to break even.`}
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:12 }}>
              <Met label="Monthly revenue" value={f$(whatIfRevenue)} color={COLORS.primary} />
              <Met label="Surplus / deficit" value={`${surplus>=0?"+":""}${f$(surplus)}`} sub={`${surplus>=0?"+":""}${f$(surplus*12)}/yr`} color={surplus>=0?COLORS.accent:COLORS.danger} bg={surplus>=0?COLORS.accentPale:COLORS.dangerPale} />
              <Met label="Occ. at break-even" value={`${beOcc}%`} sub={`of ${totalCap} seats`} color={beOcc<=Math.round(curOccPct)?COLORS.accent:COLORS.warning} />
            </div>
            <div style={panel}>
              {secLabel("Occupancy vs break-even")}
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:COLORS.textMuted, marginBottom:4 }}>
                <span>0</span>
                <span style={{ color:COLORS.danger, fontWeight:700 }}>break-even: {beStudents}</span>
                <span>cap: {totalCap}</span>
              </div>
              <div style={{ height:20, background:COLORS.borderLight, borderRadius:6, position:"relative", overflow:"hidden", marginBottom:4 }}>
                <div style={{ position:"absolute", left:`${markPct}%`, top:0, bottom:0, width:2, background:COLORS.danger, zIndex:2 }} />
                <div style={{ position:"absolute", left:0, top:0, bottom:0, width:`${curOccPct}%`, background:profitable?COLORS.accent:COLORS.primary, borderRadius:6, transition:"width .5s" }} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:16 }}>
                <span style={{ color:profitable?COLORS.accent:COLORS.textMuted }}>current: {totalEnrolledIncTest} ({Math.round(curOccPct)}%)</span>
                <span style={{ fontWeight:700, color:profitable?COLORS.accent:COLORS.warning }}>{profitable?"profitable ✓":`${beStudents-totalEnrolledIncTest} to go`}</span>
              </div>
              {secLabel("Revenue at different occupancy levels")}
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead><tr style={{ borderBottom:`1px solid ${COLORS.border}` }}>
                  {["Occupancy","Students","Revenue","vs expenses","Status"].map(h => (
                    <th key={h} style={{ padding:"5px 8px", textAlign:"left", fontSize:10, fontWeight:700, color:COLORS.textMuted, textTransform:"uppercase" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[50,60,70,80,90,100].map(p => {
                    const st=Math.round(totalCap*p/100), rv=st*avgPS, su=rv-expenses;
                    const isCur=(p-10<curOccPct&&curOccPct<=p)||(p===50&&curOccPct<=50);
                    return (
                      <tr key={p} style={{ borderBottom:`1px solid ${COLORS.borderLight}`, background:isCur?COLORS.primaryPale:"transparent" }}>
                        <td style={{ padding:"6px 8px", fontWeight:isCur?700:400, color:isCur?COLORS.primary:COLORS.text }}>{p}%{isCur?" ←":""}</td>
                        <td style={{ padding:"6px 8px", color:COLORS.textMuted }}>{st}</td>
                        <td style={{ padding:"6px 8px" }}>{f$(rv)}</td>
                        <td style={{ padding:"6px 8px", fontWeight:700, color:su>=0?COLORS.accent:COLORS.danger }}>{su>=0?"+":""}{f$(su)}</td>
                        <td style={{ padding:"6px 8px" }}>
                          <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20, background:su>=0?COLORS.accentPale:COLORS.dangerPale, color:su>=0?COLORS.accent:COLORS.danger }}>{su>=0?"profitable":"below"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastingPage;
